'use client';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { IAnalyticsAdapter } from '@/lib/analytics';

interface AnalyticsContextValue {
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  page: (pageName: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface Props {
  adapter: IAnalyticsAdapter;
  apiKey: string;
  // Pass 'Spreadd' here to group all users under one Amplitude profile (current setup).
  // Remove it entirely to switch to per-user tracking where each user gets their own profile.
  sharedUserId?: string;
  children: ReactNode;
}

export const AnalyticsProvider = ({ adapter, apiKey, sharedUserId, children }: Props) => {
  // Initialise the adapter once. The idempotency guard lives in AmplitudeAdapter.init,
  // but useEffect (not useMemo) is the right place for side-effects in React 18.
  useEffect(() => {
    adapter.init({ apiKey, sharedUserId });
  }, [adapter, apiKey, sharedUserId]);

  // This ref holds the real user's info (their actual moodle ID, username, fullname).
  // We can't use state here because updating state would cause a re-render and rebuild
  // the track/identify functions unnecessarily. A ref lets us read the latest value
  // inside the memoised callbacks without any of that overhead.
  const userPropsRef = useRef<Record<string, unknown>>({});

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      // Merge the current user's info into every event so you can filter by username
      // in Amplitude even though everything sits under the shared 'Spreadd' profile.
      track: (event, props) => adapter.track(event, { ...userPropsRef.current, ...props }),

      identify: (userId, traits) => {
        if (sharedUserId) {
          // In shared mode we don't want Amplitude to switch user profiles on each login.
          // Instead we save the real user's details here and let track() carry them as
          // event properties — so per-user filtering still works in the dashboard.
          userPropsRef.current = { userId, ...traits };
        }
        // If sharedUserId is set this call just updates user properties on the shared profile.
        // If it's not set, this properly identifies the individual user in Amplitude.
        adapter.identify(sharedUserId ?? userId, traits);
      },

      page: (pageName, props) => adapter.page(pageName, { ...userPropsRef.current, ...props }),

      reset: () => {
        // Clear stored user info on logout so the next session starts clean.
        userPropsRef.current = {};
        adapter.reset();
      },
    }),
    [adapter, sharedUserId]
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (): AnalyticsContextValue => {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalytics must be used inside AnalyticsProvider');
  return ctx;
};
